import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMfgApi, errMsg } from './mfgApi';
import { useToast } from '../../hooks/useToast';
import { Factory, AlertTriangle, Package, Truck, Plus, Loader2 } from 'lucide-react';
import { useMfgRole, MfgReadOnlyBadge } from './useMfgRole';

export default function MfgDashboard() {
  const mfg = useMfgApi();
  const toast = useToast();
  const navigate = useNavigate();
  const { isOwner, canWrite } = useMfgRole();
  const [loading, setLoading] = useState(true);
  const [dpr, setDpr] = useState(null);
  const [monthly, setMonthly] = useState(null);

  const today = new Date().toISOString().split('T')[0];
  const month = today.slice(0, 7);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, m] = await Promise.all([
        mfg.get('/reports/dpr', { date: today }).catch(() => null),
        mfg.get('/reports/monthly-plan', { month }).catch(() => null),
      ]);
      setDpr(d); setMonthly(m);
    } catch {} finally { setLoading(false); }
  }, [today, month]);

  useEffect(() => { load(); }, [load]);

  const gt = dpr?.grand_totals || {};
  const activeOrders = monthly?.totals?.total_orders || 0;
  const overdueOrders = monthly?.overdue_count || 0;
  const todayProd = gt.total_production_pieces || 0;
  const todayDisp = gt.total_dispatch_cartons || 0;

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="mfg-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900 flex items-center gap-2"><Factory size={24} className="text-indigo-500" /> Manufacturing</h1>
          <p className="text-sm text-gray-500 mt-1 flex items-center gap-2">Production Planner Dashboard<MfgReadOnlyBadge /></p>
        </div>
        <div className="flex gap-2">
          {isOwner && <button data-testid="mfg-quick-order" onClick={() => navigate('/mfg/orders?new=1')} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-indigo-500 text-white text-xs font-bold hover:bg-indigo-600 transition-colors"><Plus size={14} /> New Order</button>}
          {canWrite && <button data-testid="mfg-quick-prod" onClick={() => navigate('/mfg/production?new=1')} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-600 transition-colors"><Plus size={14} /> Record Production</button>}
          {canWrite && <button data-testid="mfg-quick-disp" onClick={() => navigate('/mfg/dispatch?new=1')} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-cyan-500 text-white text-xs font-bold hover:bg-cyan-600 transition-colors"><Plus size={14} /> Record Dispatch</button>}
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiCard label="Active Orders" value={activeOrders} icon={Package} color="indigo" testId="kpi-active-orders" />
        <KpiCard label="Overdue Orders" value={overdueOrders} icon={AlertTriangle} color={overdueOrders > 0 ? 'red' : 'gray'} testId="kpi-overdue" />
        <KpiCard label="Today's Production" value={`${todayProd.toLocaleString()} pcs`} icon={Factory} color="emerald" testId="kpi-today-prod" />
        <KpiCard label="Today's Dispatch" value={`${todayDisp.toLocaleString()} ctns`} icon={Truck} color="cyan" testId="kpi-today-disp" />
      </div>

      {/* DPR summary */}
      <div className="glass-card p-5">
        <h2 className="text-sm font-bold text-gray-900 mb-3">Today's DPR Summary — {today}</h2>
        {(dpr?.units || []).length === 0 ? (
          <p className="text-sm text-gray-400 py-8 text-center">No production recorded today.</p>
        ) : (
          <table className="w-full text-sm" data-testid="dpr-summary-table">
            <thead><tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-100">
              <th className="text-left py-2 px-2">Unit</th><th className="text-left py-2 px-2">Order</th><th className="text-left py-2 px-2">SKU</th>
              <th className="text-right py-2 px-2">Produced (pcs)</th><th className="text-right py-2 px-2">Dispatched (ctns)</th>
            </tr></thead>
            <tbody>
              {(dpr?.units || []).map(u => (u.orders || []).map((o, oi) => (
                <tr key={`${u.unit_id}-${oi}`} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="py-2 px-2 font-medium text-gray-900">{oi === 0 ? u.unit_name : ''}</td>
                  <td className="py-2 px-2 text-gray-600">{o.order_no}</td>
                  <td className="py-2 px-2 text-gray-600">{o.sku_name}</td>
                  <td className="py-2 px-2 text-right font-semibold text-gray-900">{o.production_pieces?.toLocaleString() || 0}</td>
                  <td className="py-2 px-2 text-right font-semibold text-gray-900">{o.dispatch_cartons?.toLocaleString() || 0}</td>
                </tr>
              )))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, color, testId }) {
  const bg = { indigo: 'bg-indigo-50', emerald: 'bg-emerald-50', cyan: 'bg-cyan-50', red: 'bg-red-50', gray: 'bg-gray-50' }[color] || 'bg-gray-50';
  const ic = { indigo: 'text-indigo-500', emerald: 'text-emerald-500', cyan: 'text-cyan-500', red: 'text-red-500', gray: 'text-gray-400' }[color] || 'text-gray-400';
  const vc = { indigo: 'text-indigo-700', emerald: 'text-emerald-700', cyan: 'text-cyan-700', red: 'text-red-700', gray: 'text-gray-500' }[color] || 'text-gray-500';
  return (
    <div className={`glass-card p-4 ${bg} border-0`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-1"><Icon size={16} className={ic} /><span className="text-[10px] uppercase font-bold tracking-wider text-gray-500">{label}</span></div>
      <p className={`text-xl font-extrabold ${vc}`}>{value}</p>
    </div>
  );
}
